import { useTranslation } from '@firmable/i18n';
import { Close } from '@firmable/icons';
import * as Toast from '@radix-ui/react-toast';
import { Notification as NotificationStore } from '@web/store/Notification';
import React from 'react';

function NotificationItem({ id, message }: { id: string; message: string }) {
  const { t } = useTranslation('common');
  const clearNotification = NotificationStore.use(({ clearNotification }) => clearNotification);
  const [open, setOpen] = React.useState(true);

  const onOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setOpen(false);
        // Delay removal to let the exit animation complete
        setTimeout(() => clearNotification(id), 150);
      }
    },
    [clearNotification, id],
  );

  const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Toast.Root
      className="bg-surface-darker rounded-radius-md shadow-overlay p-lg flex justify-between items-center data-[state=open]:animate-toastSlideDown data-[state=closed]:animate-toastSlideUp data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut motion-reduce:animate-none"
      duration={3000}
      open={open}
      onOpenChange={onOpenChange}>
      <Toast.Title className="overflow-hidden text-white break-words text-body-large">{message}</Toast.Title>
      <Toast.Action className="text-white cursor-pointer" altText={t('notifications.closeNotification')} onClick={handleClose}>
        <Close />
      </Toast.Action>
    </Toast.Root>
  );
}

function Notification() {
  const notifications = NotificationStore.use(({ notifications }) => notifications);

  return (
    <Toast.Provider>
      {!!notifications &&
        Object.keys(notifications).map((key) => (
          <NotificationItem key={key} id={key} message={notifications[key]} />
        ))}
      <Toast.Viewport className="[--viewport-padding:_25px] fixed top-0 left-1/2 -translate-x-1/2 flex flex-col p-[var(--viewport-padding)] gap-sm w-[390px] max-w-[100vw] m-0 list-none z-[2147483647] outline-none" />
    </Toast.Provider>
  );
}

export { Notification };
